import { useState } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { ROTAS } from '@remoto/shared';
import { chamar } from '../../api';
import { Modal } from '../Modal';
import { anexoNaMaquinaDesligada, FRASE_DA_MAQUINA_DESLIGADA } from './falta-do-anexo';

/** O endereço de uma imagem, com a conta das tentativas morando junto, no cache. */
interface EnderecoDaImagem {
  url: string;
  /** 1 depois que um `onError` já pediu um endereço novo. */
  tentativa?: number;
  /** A segunda falha seguida: a imagem não vem mais, e a tela diz isso. */
  indisponivel?: boolean;
}

/**
 * O endereço de uma imagem, com **no máximo uma** nova tentativa depois de falhar.
 *
 * O defeito medido em produção (01/10): o `onError` da `<img>` invalidava a
 * consulta, o endereço novo trocava o `src`, a imagem falhava de novo, e assim
 * para sempre — ~90 bilhetes por minuto por UMA imagem apagada, quase metade do
 * tráfego de uma aba, com o WebSocket dela caindo a cada ~45 s. No ensaio, sem
 * a rede para frear, foram 1.971 em 5 segundos.
 *
 * A conta mora no CACHE, e não num `useState`: a conversa remonta sozinha a
 * cada giro das consultas, e um contador do componente zeraria a cada
 * remontagem — o laço voltaria, só que mais devagar. Uma tentativa nova é o
 * caso legítimo do bilhete vencido; a segunda falha seguida é a imagem que não
 * existe mais. Carregar zera a conta.
 */
function usarEnderecoComUmaTentativa(
  chave: QueryKey,
  buscar: () => Promise<{ url: string }>,
  validadeMs: number,
) {
  const cliente = useQueryClient();
  const consulta = useQuery<EnderecoDaImagem>({
    queryKey: chave,
    queryFn: buscar,
    staleTime: validadeMs,
    gcTime: validadeMs,
    retry: false,
  });

  const aoFalhar = (): void => {
    const atual = cliente.getQueryData<EnderecoDaImagem>(chave);
    if (!atual || atual.indisponivel) return;
    if ((atual.tentativa ?? 0) >= 1) {
      cliente.setQueryData<EnderecoDaImagem>(chave, { ...atual, indisponivel: true });
      return;
    }
    void cliente.fetchQuery<EnderecoDaImagem>({
      queryKey: chave,
      queryFn: async () => ({ ...(await buscar()), tentativa: 1 }),
      staleTime: 0,
    });
  };

  const aoCarregar = (): void => {
    const atual = cliente.getQueryData<EnderecoDaImagem>(chave);
    if (atual?.tentativa) cliente.setQueryData<EnderecoDaImagem>(chave, { url: atual.url });
  };

  return { ...consulta, aoFalhar, aoCarregar };
}

/**
 * A URL assinada vale dez minutos; guardamos por oito.
 *
 * Pedir uma nova a cada render trocava o `src` do `<img>` e o navegador
 * recarregava a imagem do zero — do lado de quem olha, a foto piscava sozinha
 * de tempos em tempos. Uma janela menor que a validade deixa margem para a
 * imagem terminar de carregar depois de pedida.
 */
const VALIDADE_MS = 8 * 60_000;

/**
 * Uma imagem citada na conversa, mostrada como imagem.
 *
 * Antes ela aparecia como o caminho do arquivo na máquina
 * (`C:\Users\...\anexos\<uuid>.png`), sublinhado como link — e o link não abria
 * nada: a pasta de anexos do daemon fica **de propósito** fora da allowlist de
 * raízes, então não há como lê-la pelo caminho. Quem manda uma foto para a
 * sessão quer ver a foto, não o endereço dela.
 *
 * Os bytes vêm do armazenamento do painel, que é de onde eles saíram: o
 * navegador enviou o arquivo para lá, a máquina baixou de lá. Buscar da origem
 * funciona mesmo com a máquina desligada, e não abre um centímetro a mais do
 * disco dela.
 */
export function ImagemDaConversa({
  sessaoId,
  nome,
  legenda,
}: {
  sessaoId: string;
  /** Nome do arquivo; o servidor monta a chave a partir da sessão. */
  nome: string;
  legenda: string;
}): React.JSX.Element {
  const [aberta, setAberta] = useState(false);

  /**
   * Por consulta com cache, e não por efeito.
   *
   * O efeito refazia o pedido a cada montagem — e a conversa remonta sozinha a
   * cada giro das consultas de sessão e permissão. Com cache, a mesma imagem
   * citada duas vezes também divide um pedido só.
   */
  const { data, isError, error, aoFalhar, aoCarregar } = usarEnderecoComUmaTentativa(
    ['anexo-url', sessaoId, nome],
    () => chamar<{ url: string }>(`${ROTAS.sessoes}/${sessaoId}/anexos/${encodeURIComponent(nome)}/url`),
    VALIDADE_MS,
  );
  const url = data?.url ?? '';

  if (isError) {
    // F6: o anexo mora na máquina da conversa, e ela está desligada — existe,
    // só não dá para buscar agora. Outra frase, e nenhum link que falha.
    if (anexoNaMaquinaDesligada(error)) {
      return (
        <span className="text-xs text-zinc-500" data-teste="anexo-maquina-desligada">
          {legenda} ({FRASE_DA_MAQUINA_DESLIGADA})
        </span>
      );
    }
    // Sem inventar: o anexo pode ter sido podado do armazenamento. Mostrar o
    // nome é mais honesto que um quadrado quebrado.
    return <span className="text-xs text-zinc-500">{legenda} (não está mais guardada)</span>;
  }

  if (data?.indisponivel) {
    // O endereço foi assinado, mas os bytes não vieram — duas vezes seguidas.
    return (
      <span className="text-xs text-zinc-500">
        {legenda} (não está mais disponível: o armazenamento não entregou o arquivo)
      </span>
    );
  }

  if (!url) {
    return (
      <span className="my-1 block h-24 w-40 animate-pulse rounded-lg bg-zinc-800/60" aria-hidden />
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setAberta(true)}
        title={'Abrir ' + legenda}
        className="my-1 block overflow-hidden rounded-lg transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
      >
        <img
          src={url}
          alt={legenda}
          /**
           * Bilhete vencido enquanto a conversa ficou aberta: pede outro, UMA
           * vez, em vez de virar quadrado quebrado. Vencer é o comportamento
           * certo do bilhete; falhar de novo com o bilhete novo é a imagem que
           * não existe mais (ver `usarEnderecoComUmaTentativa`).
           */
          onError={aoFalhar}
          onLoad={aoCarregar}
          /**
           * Teto de altura, não de largura: uma captura de tela inteira empurra
           * a conversa para fora da tela, e o que interessa na miniatura é
           * reconhecer a imagem — ler, se precisar, é no tamanho grande.
           */
          className="max-h-56 w-auto max-w-full rounded-lg object-contain"
        />
      </button>

      <Modal titulo={legenda} aberto={aberta} aoFechar={() => setAberta(false)}>
        {/*
          `max-h` em unidade de tela: dentro do modal a imagem precisa caber na
          janela, senão o diálogo cresce e some para fora dela.
        */}
        <img
          src={url}
          alt={legenda}
          className="mx-auto max-h-[75vh] w-auto max-w-full rounded object-contain"
        />
        <p className="mt-3 text-center text-xs text-zinc-500">
          <a href={url} target="_blank" rel="noreferrer" className="underline">
            abrir em outra aba
          </a>
        </p>
      </Modal>
    </>
  );
}

/** Extensões que o painel desenha como imagem. */
const EXTENSOES = /\.(png|jpe?g|gif|webp|avif|bmp)$/i;

export function ehImagem(caminho: string): boolean {
  return EXTENSOES.test(caminho.trim());
}

/**
 * Os dois nomes de pasta que o daemon produz, e só eles.
 *
 * `pastaDeAnexos()` (apps/agent/src/sdk/anexos.ts) responde por sistema: com
 * `LOCALAPPDATA` grava em `...\Remoto\anexos\`, e sem ele — Linux, que é o caso
 * da VPS — cai no padrão `join(tmpdir(), 'remoto-anexos')`. Só a primeira forma
 * era reconhecida aqui, então **toda sessão da VPS mostrava o caminho no lugar
 * da imagem**; as do PC funcionavam, e por isso o defeito passou batido.
 *
 * Comparação exata, e não sufixo: `/tmp/meus-anexos/` não é pasta do daemon.
 */
const PASTAS_DE_ANEXO = new Set(['anexos', 'remoto-anexos']);

/**
 * O nome do arquivo dentro da pasta de anexos, se o caminho for de um anexo.
 *
 * O daemon grava o anexo com o **último pedaço da chave** do armazenamento
 * (`<uuid>.<ext>`), numa pasta de anexos dele. É isso que permite ao painel
 * reconstruir de qual objeto aquele caminho veio, sem inventar nada: quem monta
 * a chave de verdade, com a sessão dentro, é o servidor — e é ele também quem
 * recusa o que não existe. Acertar a pasta aqui só decide se vale a pena
 * perguntar.
 */
export function nomeDeAnexo(caminho: string): string | null {
  const partes = caminho.trim().split(/[\\/]/);
  const nome = partes.pop() ?? '';
  const pasta = partes.pop() ?? '';
  if (!PASTAS_DE_ANEXO.has(pasta.toLowerCase())) return null;
  return /^[A-Za-z0-9-]{1,64}\.[A-Za-z0-9]{1,8}$/.test(nome) ? nome : null;
}

/**
 * A imagem que uma ferramenta leu ou escreveu.
 *
 * Duas origens diferentes, e a diferença importa: se o caminho é de um anexo do
 * painel, os bytes estão no armazenamento (e a pasta de anexos do daemon está
 * fora da allowlist, então não há outro jeito). Se é um arquivo do projeto,
 * quem serve é a própria máquina, pelo relay — que respeita a allowlist e
 * continua sendo a única barreira do disco dela.
 *
 * Sem máquina à mão e sem ser anexo, não mostra nada: inventar um caminho para
 * o relay seria pedir arquivo que ninguém autorizou.
 */
export function ImagemDaFerramenta({
  caminho,
  sessaoId,
  maquinaId,
}: {
  caminho: string;
  sessaoId?: string;
  maquinaId?: string;
}): React.JSX.Element | null {
  const anexo = nomeDeAnexo(caminho);
  if (anexo && sessaoId) {
    return <ImagemDaConversa sessaoId={sessaoId} nome={anexo} legenda="imagem lida" />;
  }
  if (!maquinaId) return null;
  return <ImagemDaMaquina maquinaId={maquinaId} caminho={caminho} />;
}

/**
 * Imagem servida pela máquina, pelo relay de mídia.
 *
 * O bilhete vale cinco minutos e é preso a um arquivo só — `<img>` não sabe
 * mandar cabeçalho de autenticação, e é por isso que ele existe. O endereço
 * fica em cache pelo mesmo motivo da imagem anexada: pedir outro a cada render
 * trocaria o `src` e a imagem piscaria sozinha.
 */
function ImagemDaMaquina({
  maquinaId,
  caminho,
}: {
  maquinaId: string;
  caminho: string;
}): React.JSX.Element | null {
  const [aberta, setAberta] = useState(false);

  const { data, isError, aoFalhar, aoCarregar } = usarEnderecoComUmaTentativa(
    ['midia-arquivo', maquinaId, caminho],
    async () => {
      const r = await chamar<{ token: string }>(`${ROTAS.maquinas}/${maquinaId}/arquivo/token`, {
        method: 'POST',
        corpo: { caminho },
      });
      return { url: `/api/arquivos/fluxo/${r.token}` };
    },
    // O bilhete do relay dura cinco minutos; quatro deixam margem.
    4 * 60_000,
  );

  // O bilhete foi recusado (fora da allowlist, máquina desligada): o cartão da
  // ferramenta já mostra o caminho, então some sem barulho em vez de acusar.
  if (isError || !data?.url) return null;

  if (data.indisponivel) {
    // O bilhete saiu, mas a máquina não entregou o arquivo — duas vezes seguidas.
    return (
      <span className="mt-2 block text-xs text-zinc-500">
        A imagem não está mais disponível: a máquina não entregou o arquivo (apagado, fora das
        pastas liberadas ou máquina desligada).
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setAberta(true)}
        title={'Abrir ' + caminho}
        className="mt-2 block overflow-hidden rounded-lg transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
      >
        <img
          src={data.url}
          alt={caminho}
          onError={aoFalhar}
          onLoad={aoCarregar}
          className="max-h-56 w-auto max-w-full rounded-lg object-contain"
        />
      </button>

      <Modal titulo={caminho} aberto={aberta} aoFechar={() => setAberta(false)}>
        <img
          src={data.url}
          alt={caminho}
          className="mx-auto max-h-[75vh] w-auto max-w-full rounded object-contain"
        />
      </Modal>
    </>
  );
}
